import type { ChapterBackupRecord } from '@/storage/types'

export type ChapterSyncPhase =
  | 'dirty'
  | 'uploading'
  | 'synced_grace'
  | 'offline_mirror'
  | 'needs_editor'
  | 'conflict'
  | 'failed'
  | 'auth_blocked'
  | 'offline'

/** Grace period before removing local copies after a successful sync (normal projects). */
export const SYNC_GRACE_MS = 72 * 60 * 60 * 1000

const ACTIVE_PHASES: ReadonlySet<ChapterSyncPhase> = new Set([
  'dirty',
  'uploading',
  'failed',
  'needs_editor',
  'conflict',
  'auth_blocked',
  'offline',
])

const VISIBLE_ACTIVITY_PHASES: ReadonlySet<ChapterSyncPhase> = new Set([
  'dirty',
  'uploading',
  'failed',
  'needs_editor',
  'conflict',
  'auth_blocked',
  'offline',
])

/** Phases the background worker may pick up for PATCH retries. */
const QUEUE_UPLOAD_PHASES: ReadonlySet<ChapterSyncPhase> = new Set(['dirty', 'failed'])

export function resolveSyncPhase(record: ChapterBackupRecord): ChapterSyncPhase {
  if (record.syncPhase) {
    return record.syncPhase
  }
  if (record.pendingSync === false) {
    return record.retained ? 'offline_mirror' : 'synced_grace'
  }
  return 'dirty'
}

export function isActiveSyncPhase(phase: ChapterSyncPhase): boolean {
  return ACTIVE_PHASES.has(phase)
}

export function hasVisibleSyncActivity(phase: ChapterSyncPhase): boolean {
  return VISIBLE_ACTIVITY_PHASES.has(phase)
}

export function isQueueEligibleSyncPhase(phase: ChapterSyncPhase): boolean {
  return QUEUE_UPLOAD_PHASES.has(phase)
}

export function pendingSyncFromPhase(phase: ChapterSyncPhase): boolean {
  return isActiveSyncPhase(phase)
}

export function isGraceExpired(record: ChapterBackupRecord, now = Date.now()): boolean {
  const phase = resolveSyncPhase(record)
  if (phase !== 'synced_grace') return false
  const graceStart = record.lastSuccessfulSyncAt ?? record.timestamp
  return now - graceStart > SYNC_GRACE_MS
}

export function isBackupPendingUpload(
  record: Pick<ChapterBackupRecord, 'pendingSync' | 'retained' | 'syncPhase'>,
): boolean {
  return pendingSyncFromPhase(resolveSyncPhase(record as ChapterBackupRecord))
}
